import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, } from "react";
import { getClassName } from "@sscs/utils";
import { CodeIcon, FormatBoldIcon, FormatItalicIcon, FormatListBulletedIcon, FormatListNumberedIcon, FormatQuoteIcon, RedoIcon, TitleIcon, UndoIcon, WrapTextIcon, } from "../../icons";
import { IconButton, VerticalDivider } from "../index";
import styles from "./markdownEditor.module.scss";
import { prefixLines, wrap, wrapCode, } from "./utils";
const applyTransform = (textarea, transform) => {
    const { replaceStart, replaceEnd, replacement, cursorPosition } = transform(textarea.value, textarea.selectionStart, textarea.selectionEnd);
    textarea.setSelectionRange(replaceStart, replaceEnd);
    document.execCommand("insertText", false, replacement);
    if (cursorPosition !== undefined) {
        textarea.setSelectionRange(cursorPosition, cursorPosition);
    }
};
const TOOLBAR_GROUPS = [
    [
        {
            icon: React.createElement(TitleIcon, null),
            label: "Heading",
            textTransform: prefixLines("## "),
        },
        {
            icon: React.createElement(FormatBoldIcon, null),
            label: "Bold",
            textTransform: wrap("**", "**"),
        },
        {
            icon: React.createElement(FormatItalicIcon, null),
            label: "Italic",
            textTransform: wrap("_", "_"),
        },
        {
            icon: React.createElement(FormatQuoteIcon, null),
            label: "Quote",
            textTransform: prefixLines("> "),
        },
        {
            icon: React.createElement(CodeIcon, null),
            label: "Code",
            textTransform: wrapCode,
        },
    ],
    [
        {
            icon: React.createElement(FormatListNumberedIcon, null),
            label: "Numbered list",
            textTransform: prefixLines((line, index) => `${index + 1}. ${line}`),
        },
        {
            icon: React.createElement(FormatListBulletedIcon, null),
            label: "Bullet list",
            textTransform: prefixLines("- "),
        },
    ],
    [
        { icon: React.createElement(UndoIcon, null), label: "Undo", command: "undo" },
        { icon: React.createElement(RedoIcon, null), label: "Redo", command: "redo" },
    ],
];
const MarkdownEditor = ({ toolbar = true, className, textareaProps, }) => {
    const { className: textareaClassName, onKeyDown: textAreaOnKeyDown, ...restTextareaProps } = textareaProps !== null && textareaProps !== void 0 ? textareaProps : {};
    const textareaRef = useRef(null);
    const mirrorRef = useRef(null);
    const [lines, setLines] = useState("1");
    const [wrapLines, setWrapLines] = useState(true);
    const recomputeLineNumbers = useCallback(() => {
        if (!textareaRef.current || !mirrorRef.current)
            return;
        const { lineHeight, paddingLeft, paddingRight } = window.getComputedStyle(textareaRef.current);
        const parsedLineHeight = parseFloat(lineHeight);
        if (isNaN(parsedLineHeight))
            return;
        mirrorRef.current.style.width =
            textareaRef.current.clientWidth -
                parseFloat(paddingLeft) -
                parseFloat(paddingRight) +
                "px";
        setLines(textareaRef.current.value
            .split("\n")
            .flatMap((line, index) => {
            mirrorRef.current.textContent = line || " ";
            return Array.from({
                length: Math.ceil(mirrorRef.current.scrollHeight / parsedLineHeight),
            }, (_, wrappedRow) => (wrappedRow === 0 ? (index + 1).toString() : ""));
        })
            .join("\n"));
    }, []);
    useEffect(() => {
        if (!textareaRef.current)
            return;
        const resizeObserver = new ResizeObserver(recomputeLineNumbers);
        resizeObserver.observe(textareaRef.current);
        return () => resizeObserver.disconnect();
    }, [recomputeLineNumbers]);
    useLayoutEffect(recomputeLineNumbers, [
        recomputeLineNumbers,
        restTextareaProps.value,
        wrapLines,
    ]);
    return (React.createElement("div", { className: getClassName(styles.root, className) },
        React.createElement("div", { ref: mirrorRef, className: getClassName(styles["measure-mirror"], {
                [styles["measure-mirror-no-wrap"]]: !wrapLines,
            }), "aria-hidden": "true" }),
        toolbar && (React.createElement("div", { className: styles.toolbar, role: "toolbar", "aria-label": "Markdown formatting" },
            TOOLBAR_GROUPS.map((group, groupIndex) => (React.createElement(React.Fragment, { key: groupIndex },
                groupIndex > 0 && React.createElement(VerticalDivider, null),
                group.map((toolbarButton) => (React.createElement(IconButton, { key: toolbarButton.label, size: "small", tooltipTitle: toolbarButton.label, "aria-label": toolbarButton.label, className: styles["toolbar-button"], onMouseDown: (event) => {
                        event.preventDefault();
                        if ("command" in toolbarButton) {
                            document.execCommand(toolbarButton.command);
                        }
                        else if (textareaRef.current) {
                            applyTransform(textareaRef.current, toolbarButton.textTransform);
                        }
                    } }, toolbarButton.icon)))))),
            React.createElement(VerticalDivider, null),
            React.createElement(IconButton, { size: "small", tooltipTitle: "Wrap lines", "aria-label": "Wrap lines", "aria-pressed": wrapLines, className: styles["toolbar-button"], onClick: () => setWrapLines((previous) => !previous) },
                React.createElement(WrapTextIcon, null)))),
        React.createElement("div", { className: styles.wrapper },
            React.createElement("div", { className: styles["line-numbers"], "aria-hidden": "true" }, lines),
            React.createElement("textarea", { ref: textareaRef, wrap: wrapLines ? "soft" : "off", className: getClassName(styles["text-area"], textareaClassName), onKeyDown: (event) => {
                    if (event.key !== "Enter" ||
                        !textareaRef.current ||
                        textareaRef.current.selectionStart !==
                            textareaRef.current.selectionEnd) {
                        return;
                    }
                    const lineStart = textareaRef.current.value.lastIndexOf("\n", textareaRef.current.selectionStart - 1) + 1;
                    const currentLine = textareaRef.current.value.slice(lineStart, textareaRef.current.selectionStart);
                    const bulletMatch = /^\s*[-*+] /.exec(currentLine);
                    const numberedMatch = /^(\s*)(\d+)(\. )/.exec(currentLine);
                    const cursorPos = textareaRef.current.selectionStart;
                    let replaceStart = cursorPos;
                    let replacement = null;
                    if (currentLine === (bulletMatch === null || bulletMatch === void 0 ? void 0 : bulletMatch[0]) ||
                        currentLine === (numberedMatch === null || numberedMatch === void 0 ? void 0 : numberedMatch[0])) {
                        replaceStart = lineStart;
                        replacement = "\n";
                    }
                    else if (bulletMatch) {
                        replacement = "\n" + bulletMatch;
                    }
                    else if (numberedMatch) {
                        const [, listIndent, listNumber, listSeparator] = numberedMatch;
                        replacement =
                            "\n" + listIndent + (parseInt(listNumber) + 1) + listSeparator;
                    }
                    if (replacement !== null) {
                        event.preventDefault();
                        textareaRef.current.setSelectionRange(replaceStart, cursorPos);
                        document.execCommand("insertText", false, replacement);
                    }
                    textAreaOnKeyDown === null || textAreaOnKeyDown === void 0 ? void 0 : textAreaOnKeyDown(event);
                }, ...restTextareaProps }))));
};
export default MarkdownEditor;
//# sourceMappingURL=MarkdownEditor.js.map